import { ReactComponent as TrashIcon } from "@/assets/icons/common/trash-icon.svg";
import { SalesProductTypeOptions, ServiceProductTypeOptions } from "@/Common/constants/ProductTypeOptions";
import SimpleIconButton from "@/Layouts/components/Buttons/SimpleIconButton";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import { SALES_POSITIONS } from "@/Settings/Constants/SystemSettings";
import { IGrossChargebackType } from "@/Settings/types/Common/model";
import SimpleDoubleChecking from "@Layouts/components/Buttons/SimpleDoubleChecking";
import { AddCircleRounded } from "@mui/icons-material";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, Stack } from "@mui/material";
import { useMemo } from "react";

const containerSx = {
	"& > *:not(:last-child) .btn-add": { display: "none" }, // display add button only for last item
};

export const EMPTY_GROSS_CHARGEBACK_ITEM = {
	chargebackCode: "",
	productType: "",
} as IGrossChargebackType;

interface IGrossChargebackSelectorProps {
	items: IGrossChargebackType[];
	selectableOptions: { code: string; description: string }[];
	isPending: boolean;
	position: string;
	onAdd: () => void;
	onChange: (item: IGrossChargebackType, index: number) => void;
	onDelete: (index: number) => void;
}

const GrossChargebackSelector = (props: IGrossChargebackSelectorProps) => {
	const { items, selectableOptions, isPending, onAdd, onChange, onDelete, position } = props;

	const displayItems = items.length === 0 ? [] : items;

	const getAvailableChargebackOptions = (currentIndex: number) => {
		const selectedCodes = items.map((item, idx) => (idx !== currentIndex ? String(item.chargebackCode) : null)).filter(Boolean);
		return selectableOptions.filter((option) => !selectedCodes.includes(option.code));
	};

	const addButtonEnabled = useMemo(() => {
		if (items.length === 0) return true;
		const lastItem = items.at(-1)!;
		const hasValidLastItem = !!lastItem.chargebackCode && !!lastItem.productType;
		const hasAvailableOptions = !!getAvailableChargebackOptions(items.length).length;
		return hasValidLastItem && hasAvailableOptions;
	}, [items, selectableOptions]);

	const isSalesDepartment = SALES_POSITIONS.includes(position);

	const placeholderText = isSalesDepartment ? "Frontend/Backend" : "Labor/Parts";
	const productTypeOptions = isSalesDepartment
		? Object.entries(SalesProductTypeOptions).map(([value, { label }]) => ({ value, label }))
		: Object.entries(ServiceProductTypeOptions).map(([value, { label }]) => ({ value, label }));

	return (
		<Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={containerSx}>
			{displayItems.map((item, index) => (
				<Stack key={index} direction="column">
					<Stack direction="row" alignItems="center">
						<Box sx={{ width: 216, mr: 1.25 }}>
							<SmartSelector
								showBy="description"
								orderBy="description"
								options={getAvailableChargebackOptions(index)}
								selectBy="code"
								selected={item.chargebackCode}
								inputProps={{ placeholder: "Gross Chargeback" }}
								clearable
								disabled={isPending}
								onChangeAction={(value: any) => onChange({ ...item, chargebackCode: value?.code }, index)}
								error={!item.chargebackCode}
							/>
						</Box>
						<Box sx={{ width: 140 }}>
							<SmartSelector
								showBy="label"
								orderBy="label"
								selectBy="value"
								selected={item.productType}
								options={productTypeOptions}
								clearable
								inputProps={{ placeholder: placeholderText }}
								disabled={isPending}
								onChangeAction={(value: any) => onChange({ ...item, productType: value?.value }, index)}
								error={!item.productType}
							/>
						</Box>
						<SimpleDoubleChecking
							disabled={isPending}
							color="var(--gray-blue-color)"
							confirmationButtonColor="var(--gray-blue-color)"
							icon={<CloseRoundedIcon fontSize="small" />}
							iconHover={<CloseRoundedIcon fontSize="small" />}
							confirmationIcon={<TrashIcon />}
							onDeleteAction={() => onDelete(index)}
						/>
					</Stack>
				</Stack>
			))}
			<SimpleIconButton
				className="btn-add !ml-[4px]"
				color="var(--primary-color)"
				disabled={isPending || !addButtonEnabled}
				icon={<AddCircleOutlineRoundedIcon fontSize="medium" />}
				iconHover={<AddCircleRounded fontSize="medium" />}
				onClick={onAdd}
			/>
		</Stack>
	);
};

export default GrossChargebackSelector;
